import { DataTable, useToast } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState, useMemo } from 'react';
import { useLocalizedStrings } from '../../../../Localization';
import { useReports, ReportData } from '../../../../Contexts/ReportsContext';

type ReportComponentConfig = {
  kind: 'Report';
  title: string;
  description?: string;
  reportId: string;
}

type ReportProps = {
  component: ReportComponentConfig;
  height?: number;
}

export const Report = ({ component, height }: ReportProps) => {
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const { getReportById, fetchReportById, generateReportDataImmediate } = useReports();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportFetched, setReportFetched] = useState(false);
  
  // Separate effect for fetching report metadata (similar to ReportViewPage)
  useEffect(() => {
    const fetchReport = async () => {
      if (!component.reportId || reportFetched) return;
      
      try {
        await fetchReportById(component.reportId);
        setReportFetched(true);
      } catch (error) {
        console.error('Error fetching report:', error);
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report,
          variant: 'error'
        });
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [component.reportId, reportFetched]);

  // Separate effect for loading report data when report config is available
  useEffect(() => {
    const loadReportData = async () => {
      if (!reportFetched || !component.reportId) {
        return;
      }

      const report = getReportById(component.reportId);
      if (!report) {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.report_not_found,
          variant: 'error'
        });
        setIsLoading(false);
        return;
      }

      if (!report.config) {
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report,
          variant: 'error'
        });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Generate the report data using the report's config
        const data = await generateReportDataImmediate({
          config: report.config,
          page: 1,
          limit: 100, // Show first 100 rows in dashboard
          forceRefresh: false
        });

        if (data) {
          setReportData(data);
        } else {
          toast({
            title: L.admin_panel.reports.error,
            description: L.admin_panel.reports.failed_to_load_report_data,
            variant: 'error'
          });
        }
      } catch (err) {
        console.error('Error loading report data:', err);
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report_data,
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadReportData();
  }, [reportFetched, component.reportId]);

  const { columns, data } = useMemo(() => {
    if (!reportData?.data || !Array.isArray(reportData.data) || reportData.data.length === 0) {
      return { columns: [], data: [] };
    }

    // Create columns based on the report's column configuration
    const columns: ColumnDef<Record<string, any>>[] = reportData.columns.map((col) => ({
      id: col.key,
      header: col.label,
      accessorKey: col.key,
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null) return '';
        if (typeof value === 'number') return value.toLocaleString();
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return String(value);
      }
    }));

    return {
      columns,
      data: reportData.data as Record<string, any>[]
    };
  }, [reportData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div className="text-sm font-medium">{L.admin_panel.reports.loading_report}</div>
        </div>
      </div>
    );
  }


  if (!reportData?.data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div className="text-sm font-medium">{L.admin_panel.reports.no_data_available}</div>
          <div className="text-xs">{L.admin_panel.dashboards.report_no_data}</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="w-full overflow-y-auto" 
      style={{ height: height ? `${height}px` : 'auto' }}
    >
      <DataTable
        className="p-2"
        columns={columns}
        data={data}
        compact
        noDataMessage={L.admin_panel.dashboards.report_no_data}
        initialSorting={[]}
        showPagination={false}
        pageSize={10}
        verticalAlign="middle"
      />
    </div>
  );
};